import { orderBy } from "lodash";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const KEYMAP_COLUMN_ENTITY_TOKEN = new StateToken<KeymapColumnEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_COLUMN_ENTITY);

export type KeymapColumnEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.KeymapColumnEntityModels.KeymapColumn>;

@State<KeymapColumnEntityStateModel>({
  name: KEYMAP_COLUMN_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class KeymapColumnEntityState extends HistoryEntityBaseState<FlatModels.KeymapColumnEntityModels.KeymapColumn, KeymapColumnEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([KeymapColumnEntityState])
  static override typedEntities$(state: KeymapColumnEntityStateModel): { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([KeymapColumnEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn }): FlatModels.KeymapColumnEntityModels.KeymapColumn[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([KeymapColumnEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([KeymapColumnEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.KeymapColumnEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<KeymapColumnEntityStateModel>,
                            action: FlatActions.KeymapColumnEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<KeymapColumnEntityStateModel>,
                              action: FlatActions.KeymapColumnEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<KeymapColumnEntityStateModel>,
                               action: FlatActions.KeymapColumnEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<KeymapColumnEntityStateModel>,
                                 action: FlatActions.KeymapColumnEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.KeymapColumnEntityActions.AddEntity)
  @Undoable(
    FlatActions.KeymapColumnEntityActions.AddEntity,
    KEYMAP_COLUMN_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<KeymapColumnEntityStateModel>,
                     action: FlatActions.KeymapColumnEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.KeymapColumnEntityActions.UpdateEntity,
    KEYMAP_COLUMN_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<KeymapColumnEntityStateModel>,
                        action: FlatActions.KeymapColumnEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.KeymapColumnEntityActions.RemoveEntity,
    KEYMAP_COLUMN_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<KeymapColumnEntityStateModel>,
                        action: FlatActions.KeymapColumnEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.KeymapColumnEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<KeymapColumnEntityStateModel>,
                        action: FlatActions.KeymapColumnEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<KeymapColumnEntityStateModel>,
                        action: FlatActions.KeymapColumnEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.KeymapColumnEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<KeymapColumnEntityStateModel>,
                            action: FlatActions.KeymapColumnEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }
  
  @Action(FlatActions.KeymapColumnEntityActions.RearrangeKeymapColumns)
  rearrangeKeymapColumns({getState, dispatch}: StateContext<KeymapColumnEntityStateModel>,
                         {keymapColumns}: FlatActions.KeymapColumnEntityActions.RearrangeKeymapColumns) {
    // Get Entities
    const entities = structuredClone(getState().entities);
    
    // Get Keymap Column Entitites
    const keymapColumnEntities: FlatModels.KeymapColumnEntityModels.KeymapColumn[] = [];
    keymapColumns.forEach((keymapColumn) => {
      // Get Entity
      let entity;
      if (typeof keymapColumn === 'string') {
        entity = entities[keymapColumn];
      } else {
        entity = entities[keymapColumn.id];
      }
      if (entity) {
        keymapColumnEntities.push(entity);
      }
    });
    
    // Iterate Through Keymap Columns
    let i = 0;
    for (const keymapColumn of orderBy(keymapColumnEntities, (a) => a.order || 0, 'asc')) {
      // Update Entity
      keymapColumn.order = i;
      
      // Dispatch Update
      dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn));
      
      // Increment i
      i++;
    }
  }

  //</editor-fold>

  //</editor-fold>
}
